import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();
  const nav = useNavigate();

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setValid(false);
      return;
    }
    api
      .verifyResetToken(token)
      .then((res) => {
        setValid(true);
        setEmail(res.email || "");
      })
      .catch(() => setValid(false))
      .finally(() => setVerifying(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast("Password must be at least 8 characters", "error");
      return;
    }
    if (pw !== pw2) {
      toast("Passwords do not match", "error");
      return;
    }
    setSaving(true);
    try {
      await api.confirmPasswordReset(token, pw);
      setDone(true);
      setTimeout(() => nav("/login"), 2000);
    } catch (err) {
      toast(err?.response?.data?.detail || "Reset failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen grid md:grid-cols-2"
      data-testid="page-reset-password"
    >
      <div className="hidden md:flex flex-col justify-between p-10"
        style={{ background: "linear-gradient(155deg, #0a5030 0%, #0e6939 55%, #14824a 100%)" }}>
        <div className="text-white/85">
          <div className="text-sm font-bold tracking-tight">Jero-One</div>
          <div className="text-xs uppercase tracking-widest text-white/60">Workspace</div>
        </div>
        <div className="max-w-md space-y-4 text-white">
          <h1 className="text-4xl font-bold tracking-tight leading-tight">
            Choose a new password.
          </h1>
          <p className="text-white/75 text-[15px] leading-relaxed">
            Pick something strong — at least 8 characters mixing letters,
            numbers and symbols. You&apos;ll be able to sign in immediately
            after saving.
          </p>
        </div>
        <div className="text-xs text-white/55">
          © {new Date().getFullYear()} Jero-One Technologies Ltd. · Uganda | USA | Canada
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-xs muted hover:text-[#0e6939]"
          >
            <ArrowLeft size={12} /> Back to sign in
          </Link>

          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <KeyRound size={20} /> Reset password
            </h2>
            {email && (
              <p className="text-sm muted mt-1">
                For <b>{email}</b>
              </p>
            )}
          </div>

          {verifying ? (
            <div className="text-sm muted" data-testid="verifying">Verifying link…</div>
          ) : !valid ? (
            <div
              className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm"
              data-testid="invalid-token"
            >
              <div className="font-semibold text-red-800 flex items-center gap-2">
                <XCircle size={16} /> Invalid or expired link
              </div>
              <p className="text-xs mt-1 text-red-700">
                Reset links are valid for 60 minutes and can only be used once.
                Please request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="btn btn-primary btn-sm mt-4"
              >
                Request new link
              </Link>
            </div>
          ) : done ? (
            <div
              className="rounded-lg border border-[#0e6939] bg-[#f3faf4] p-4 text-sm"
              data-testid="reset-done"
            >
              <div className="font-semibold flex items-center gap-2" style={{ color: "var(--green-700)" }}>
                <CheckCircle2 size={16} /> Password updated
              </div>
              <p className="text-xs mt-1 muted">
                Redirecting you to sign-in…
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="label">New password</label>
                <input
                  className="input"
                  type="password"
                  required
                  minLength={8}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="new-password"
                />
              </div>
              <div>
                <label className="label">Confirm new password</label>
                <input
                  className="input"
                  type="password"
                  required
                  minLength={8}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  data-testid="confirm-password"
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={saving || pw.length < 8}
                data-testid="reset-submit"
              >
                {saving ? "Saving…" : "Save new password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
